import {type ReactNode, useCallback, useState, useMemo} from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import {
  Coins, Users, Mail, Pencil, Award, Trophy, ClipboardCopy,
} from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { Separator } from '@/components/shadcn/separator'
import {
  Tooltip, TooltipContent, TooltipTrigger,
} from '@/components/shadcn/tooltip'
import { InfoCell } from '@/components/common/InfoCell'
import { Can } from '@/components/permission/Can'
import { ActionModuleTypeIcon } from '@/pages/groups/components/ActionModuleIcon'
import { RewardType } from '@/api/model/rewardType'
import {ActionModuleKind, PermissionEntityType} from '@/api/model'
import { usePermissions } from '@/hooks/usePermissions'
import type { ActionModule } from '../types'
import type { TFunction } from 'i18next'
import {useActionModuleMutations} from "@/pages/action-modules/hooks/useActionModuleMutations.ts";
import {notify} from "@/lib/notify.ts";
import {ConfirmDialog} from "@/components/common/ConfirmDialog.tsx";
import {htmlToPlainText} from "@/lib/html.ts";

function getRewardTypeLabels(t: TFunction): Record<string, string> {
  return {
    [RewardType.Points]: t('rewardType.Points'),
    [RewardType.Flames]: t('rewardType.Flames'),
    [RewardType.Nothing]: t('rewardType.Nothing'),
  }
}

export function ActionCard({
  module,
  onEdit,
  dragHandle,
  isAdmin,
  canCopy,
  companyId,
  onSaved,
}: {
  module: ActionModule
  onEdit: (m: ActionModule) => void
  isAdmin: boolean,
  canCopy: boolean,
  companyId?: number,
  /** Optional drag handle (only when the list is sortable via D&D). */
  dragHandle?: ReactNode,
  onSaved: () => void
}) {
  const { t } = useTranslation(['action-modules', 'common'])
  const [confirmCopy, setConfirmCopy] = useState(false)

  const api = useActionModuleMutations({ isAdmin: isAdmin, companyId: companyId })

  const onCopy = useCallback(async () => {
    if (module.id === undefined || module.id === null || !canCopy) return
    try {
      await api.createLiveActionModuleFromTemplate(module.id);
      notify.success(t('actionModuleTemplate.CopyFromTemplateSuccess'))
    } catch (error) {
      notify.error(t('editor.copyError'), { traceId: (error as { response?: { headers?: Record<string, string> } })?.response?.headers?.['x-trace-id'] })
    }
    onSaved()
  }, [module, api, t])

  const navigate = useNavigate()
  const { userHasEntityPermission } = usePermissions()
  // Backend `canBeEdited` says the module *can* be edited at all; the permission
  // says this user *may* (general ActionModules.Edit or a per-module grant).
  const canEdit = module.id != null && userHasEntityPermission(PermissionEntityType.ActionModules, 'Edit', module.id)
  const emails = (module.emailNotificationAddresses ?? []).filter(Boolean)
  const rewardTypeLabel = getRewardTypeLabels(t)[module.rewardType] ?? module.rewardType

  // Description is rich-text HTML — the card only shows a plain-text preview.
  const descriptionPreview = useMemo(() => htmlToPlainText(module.description), [module.description])

  // Enter the per-module detail page on the Incentivierung tab, carrying the module name as
  // a dynamic breadcrumb crumb (data, not a translation key).
  function openIncentivation() {
    navigate(`/action-modules/${module.id}/incentivation`, {
      state: { breadcrumbs: [{ label: module.name ?? '' }] },
    })
  }

  return (
    <div className="flex h-full flex-col border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-card">
      {/* Header: only the icon carries the module color (ActionModuleTypeIcon uses colorHex) */}
      <div className="h-14 flex items-center gap-3 px-4 border-b">
        <div className="flex size-8 flex-shrink-0 items-center justify-center overflow-hidden rounded bg-muted/40">
          {module.iconUrl ? (
            <img src={module.iconUrl} alt="" className="size-full object-cover" />
          ) : (
            <ActionModuleTypeIcon module={module} className="size-6" />
          )}
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <p className="font-semibold text-sm flex-1 truncate cursor-default">{module.name || '—'}</p>
          </TooltipTrigger>
          <TooltipContent>{module.name || '—'}</TooltipContent>
        </Tooltip>
        {module.canBeEdited && canEdit && (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            onClick={() => onEdit(module)}
          >
            <Pencil className="size-3.5" />
          </Button>
        )}
        {dragHandle}
      </div>

      {/* Info grid */}
      {/* Column 2 = minmax(0,max-content): as wide as its longest content (shared left
          edge), right-aligned because column 1 (1fr) fills the rest; min 0 preserves truncation. */}
      <div className="grid grid-cols-[1fr_minmax(0,max-content)] gap-x-4 gap-y-2 p-3 text-xs">
        <InfoCell icon={Coins} label={t('card.maxReward')} value={String(module.maxReward || '—')} />
        <InfoCell icon={Award} label={t('card.rewardType')} value={rewardTypeLabel} />
        <InfoCell icon={Users} label={t('card.maxUsers')} value={String(module.maxUserCanComplete ?? t('card.unlimited'))} />
        <InfoCell
          icon={Mail}
          label={t('card.emails')}
          value={emails.length > 0 ? String(emails.length) : '—'}
          tooltip={emails.length > 0 ? (
            <div className="flex flex-col gap-0.5">
              {emails.map((e, i) => <span key={i}>{e}</span>)}
            </div>
          ) : undefined}
        />
      </div>

      {/* Description preview (plain text, clamped to two lines) */}
      {descriptionPreview && (
        <>
          <Separator />
          <div className="px-3 py-2">
            <p className="text-xs text-muted-foreground leading-snug line-clamp-2">
              {descriptionPreview}
            </p>
          </div>
        </>
      )}

      {/* Entry button → per-module Incentivierung tab (gated per module). */}
      {module.id != null && module.kind == ActionModuleKind.Live && (
        <Can entityType={PermissionEntityType.ActionModules} permission="Incentivate" entityId={module.id}>
          <div className="mt-auto flex gap-2 border-t p-3 pt-3">
            <Button variant="outline" size="sm" className="flex-1" onClick={openIncentivation}>
              <Trophy className="size-3.5" />{t('card.incentivation')}
            </Button>
          </div>
        </Can>
      )}
      {module.id != null && module.kind != ActionModuleKind.Live && (
        <div className="mt-auto flex gap-2 border-t p-3 pt-3">
          {/*TODO In the future we should create and handle individual permissions for action module templates */}
          <Button size="sm" className="flex-1" disabled={!canCopy} onClick={() =>setConfirmCopy(true)}>
            <ClipboardCopy className="size-3.5"/>{t(`actionModuleKind.options.${module.kind}`)}
          </Button>
        </div>
      )}
      <ConfirmDialog
        open={confirmCopy}
        title={t('actionModuleTemplate.CopyFromTemplateConfirmTitle')}
        description={t('actionModuleTemplate.CopyFromTemplateConfirmDescription')}
        confirmLabel={t('actionModuleTemplate.CreateLiveModule')}
        confirmButtonVariant="default"
        isLoading={api.isSaving}
        onConfirm={() => {
          setConfirmCopy(false)
          onCopy()
        }}
        onClose={() => setConfirmCopy(false)}
      />
    </div>
  )
}
